import React, {useState} from 'react';
import {Spin, TreeSelect} from 'antd';
import type {Area} from '../../types';
import {useArea} from './hooks/useArea';
import {useTranslation} from '../../hooks/useTranslation';
import {API_PARAMS} from "../../constants/constants";

interface SelectAreaProps {
  id?: string;
  value?: string | string[];
  placeholder?: string;
  onChange?: (value: string | string[], label?: any, extra?: any) => void;
  disabled?: boolean;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  multiple?: boolean;
  treeCheckable?: boolean;
  showCheckedStrategy?: 'SHOW_ALL' | 'SHOW_PARENT' | 'SHOW_CHILD';
  treeDefaultExpandAll?: boolean;
  maxTagCount?: number | 'responsive';
  initialTreeData?: Area[];
}

const SelectArea: React.FC<SelectAreaProps> = ({
                                                 id,
                                                 value,
                                                 placeholder,
                                                 onChange,
                                                 disabled = false,
                                                 allowClear = true,
                                                 size = 'middle',
                                                 style,
                                                 className,
                                                 multiple,
                                                 treeCheckable = true,
                                                 showCheckedStrategy = 'SHOW_PARENT',
                                                 initialTreeData
                                               }) => {
  const {t} = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Use the useArea hook to fetch data - only when the dropdown is opened
  const {data: areas = [], isLoading, error} = useArea(
    {
      node: API_PARAMS.ROOT,
      depth: API_PARAMS.ZERO
    },
    {
      enabled: isDropdownOpen
    }
  );

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const handleChange = (selectedValue: string | string[], label?: any, extra?: any) => {
    if (onChange) {
      onChange(selectedValue, label, extra);
    }
  };

  const handleSearch = (_searchText: string) => {
    // intentionally empty; parent controls search behavior
  };

  const filterTreeNode = (inputValue: string, treeNode: any) => {
    return treeNode.title?.toLowerCase().includes(inputValue.toLowerCase()) || false;
  };

  if (error) {
    console.error('Error loading areas:', error);
  }

  const treeDataToUse = initialTreeData && initialTreeData.length ? initialTreeData : areas;

  return (
    <TreeSelect
      id={id}
      value={value}
      placeholder={placeholder || t('messages.select')}
      onChange={handleChange}
      onSearch={handleSearch}
      onOpenChange={handleDropdownVisibleChange}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
      style={style}
      className={className}
      multiple={multiple}
      treeCheckable={treeCheckable}
      showCheckedStrategy={showCheckedStrategy}
      listHeight={400}
      showSearch={true}
      filterTreeNode={filterTreeNode}
      loading={isLoading}
      notFoundContent={isLoading ? <Spin size="small"/> : t('messages.notFound')}
      treeData={treeDataToUse}
      styles={{popup: {root: {maxHeight: 600}}}}
    />
  );
};

export default SelectArea;
